import { GraphicsMaterials } from 'src/app/digital-twin/graphics/graphics-materials';
import * as THREE from '@phdsoft/three';
import { ELEMENTSTYPESIDS, Util } from '@phdsoft/common';
import { Injectable } from '@angular/core';
import { Asset } from 'src/app/platform/busines-data/Framework/Asset';
import { Graphics } from './graphics';
import { ThreeJsCleanup } from 'src/app/shared/utils/threejs-cleanup.util';
import { LinearElement } from '@app/platform/busines-data/SectionElements/StructureElements/LinearElement';
import { v4 as uuidv4 } from 'uuid';
import { FunctionalityMode } from '@app/asset-manager/asset-manager';
import { DTSelectionService } from '../selection/selection.service';
import { StructuralElement } from '@app/platform/busines-data/SectionElements/StructureElements/StructuralElement';
import { GeometryExtrusionComputer } from '../../geometry-extrusion/geometry-extrusion-computer';
import { MeshUtils } from '@app/platform/busines-data/Geometries/Utils/mesh-utils';
import { createSimpleGeometryCenterLabels2DObjects, Label2DInfo } from './labels-utils';
import { Line2 } from '../Three/Line2';
import { LineGeometry } from '../Three/LineGeometry';
import { LineMaterial } from '../Three/LineMaterial';
import type { StiffenerInsertionSession } from '@app/insertion-logic/stiffener-insertion-session';

interface Drawing {
  group: THREE.Group;
  init: boolean;
  buttonDown: boolean;
  previewLine?: THREE.Object3D;
  origx: number;
  origy: number;
  inix: number;
  endx: number;
  iniy: number;
  endy: number;
  startDrawing: boolean;
  constrainToAxis: boolean;
}

interface AreaDimensionSegment {
  label: string;
  start: THREE.Vector2;
  end: THREE.Vector2;
  fallbackStart?: THREE.Vector2;
  fallbackEnd?: THREE.Vector2;
  labelOffsetPx?: THREE.Vector2;
  showLabel?: boolean;
  useMaxLabelDistance?: boolean;
}

interface WorldDimensionSegment {
  label: string;
  start: THREE.Vector3;
  end: THREE.Vector3;
  showLabel?: boolean;
}

type AreaDimensionShapeType = 'rectangle' | 'circle' | 'ellipse';
type AreaDimensionLabelMode = 'widthHeight' | 'diameter';

interface AreaDimensionBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  shapeType: AreaDimensionShapeType;
  centerX?: number;
  centerY?: number;
  radius?: number;
  radiusX?: number;
  radiusY?: number;
  labelMode?: AreaDimensionLabelMode;
}

interface AreaDimensionScanline {
  start: THREE.Vector2;
  end: THREE.Vector2;
}

interface ViewAlignedBounds {
  right: THREE.Vector3;
  up: THREE.Vector3;
  forward: THREE.Vector3;
  minRight: number;
  maxRight: number;
  minUp: number;
  maxUp: number;
  centerForward: number;
}

interface ScreenPoint {
  x: number;
  y: number;
}

interface RulerRaycastHit {
  intersectionPoint: THREE.Vector3;
  displayPoint: THREE.Vector3;
}

@Injectable({ providedIn: 'root' })
export class GraphicsRenderRuler {
  private static readonly RULER_LINE_COLOR = 0xffffff;
  private static readonly RULER_PREVIEW_LINE_COLOR = 0xff3030;
  private static readonly RULER_LINE_WIDTH = 2.5;
  private static readonly RULER_DISPLAY_OFFSET = 0.001;
  private static readonly RULER_BORDER_REFINEMENT_STEPS = 10;
  private static readonly RULER_FALLBACK_SCAN_STEPS = 12;
  private static readonly RULER_POINT_EPSILON = 0.000001;
  private static readonly RULER_VALID_AREA_SCAN_STEP_PX = 48;
  private static readonly RULER_VALID_AREA_MAX_SCANLINES = 16;
  private static readonly RULER_VALID_AREA_MAX_SEGMENT_SAMPLES = 12;

  sceneObjs: any[];
  private asset: Asset;
  private functionalityMode: FunctionalityMode;
  private stiffenerSession: StiffenerInsertionSession | null = null;

  public registerStiffenerInsertionSession(session: StiffenerInsertionSession): () => void {
    this.pauseDrawing();
    this.stiffenerSession = session;
    return () => {
      if (this.stiffenerSession === session) {
        this.pauseDrawing();
        this.stiffenerSession = null;
      }
    };
  }
  constructor(
    private graphics: Graphics,
  ) {
    this.drawing.group.name = 'Ruler';
  }

  public drawing: Drawing = {
    group: new THREE.Group(),
    init: false,
    buttonDown: false,
    previewLine: undefined,
    origx: 0,
    origy: 0,
    inix: 0,
    endx: 0,
    iniy: 0,
    endy: 0,
    startDrawing: false,
    constrainToAxis: true,
  }

  public points: THREE.Vector3[] = [];
  public static dist: number;
  private rulerLabelInfos: Label2DInfo[] = [];
  private areaDimensionGroup: THREE.Group = new THREE.Group();
  private areaDimensionLabelInfos: Label2DInfo[] = [];

  public flagToStartDrawing() {
    if (!this.drawing.startDrawing) {
      this.drawing.startDrawing = true
      this.graphics.Controls.enabled = false
    }
  }

  public pauseDrawing(): void {
    ThreeJsCleanup.disposeObject(this.drawing.previewLine);
    this.drawing.previewLine = undefined;
    this.drawing.init = false;
    this.drawing.buttonDown = false;
    this.drawing.startDrawing = false;
    this.points = [];
    this.graphics.Controls.enabled = true;
    this.graphics.requestRender();
  }

  public updateMousePosition(event, asset: Asset, functionalityMode: FunctionalityMode):{regionId:string, sectionId:string, elementId:string,
     negativeGeometry: THREE.BufferGeometry, positiveGeometry:THREE.BufferGeometry} { //chamado toda vez que o mouse se movimenta
    if (functionalityMode === 'STIFFNERINSERTION' && !this.stiffenerSession?.canDraw) {
      this.pauseDrawing();
      return null;
    }
    if (functionalityMode === 'MEASURETOOLS' && asset?.mode !== 'SECTIONS') {
      this.clearGeometries();
      return null;
    }

    const isTouchEvent = event.changedTouches ? true : false;
    event = isTouchEvent ? event.changedTouches[0] : event;
    this.functionalityMode = functionalityMode;
    if (Util.isNullOrUndefined(event.clientX)) {
      event.clientX = event.center.x;
    }
    if (Util.isNullOrUndefined(event.clientY)) {
      event.clientY = event.center.y;
    }

    let adjustedX = isTouchEvent ? event.clientX : event.clientX- this.graphics.offsetX;
    let adjustedY = isTouchEvent ? event.clientY : event.clientY - this.graphics.offsetY;

    return this.draw(adjustedX, adjustedY, event.buttons, asset)
  }

  public setDrawingOrigin(origx: number, origy: number) {
    this.drawing.origx = origx;
    this.drawing.origy = origy;
  }

  /*
   *  @brief: 4 etapas (ifs),
   *          a primeira delimita quando começamos e as 3 ultimas são a
   *          seleção de fato, com o startdrag, stilldrag e enddrag
  */
  public draw(mouseX, mouseY, buttons, asset: Asset):{regionId:string, sectionId:string, elementId:string,
    negativeGeometry: THREE.BufferGeometry, positiveGeometry:THREE.BufferGeometry
  } {
    this.asset = asset;

    if (this.drawing.startDrawing) {
        if (buttons == 1) {
            this.drawing.startDrawing = false;
            this.drawing.init = true;
            this.drawing.inix = this.drawing.origx;
            this.drawing.iniy = this.drawing.origy;
        }
    }

    if (!this.drawing.buttonDown && buttons == 1) {
        // Start dragging
        this.drawing.inix = this.drawing.origx;
        this.drawing.iniy = this.drawing.origy;
        this.drawing.buttonDown = true;
        ThreeJsCleanup.disposeObject(this.drawing.previewLine);
        this.drawing.previewLine = undefined;
        if (this.functionalityMode !== "MEASURETOOLS") {
          ThreeJsCleanup.disposeObject(this.drawing.group);
          this.resetRulerGroup();
        }
    } else if (this.drawing.buttonDown && buttons == 1 && this.drawing.init) {
        // Still dragging
        ThreeJsCleanup.disposeObject(this.drawing.previewLine);
        this.drawing.previewLine = undefined;

        const distz = 1; // Distance from the camera
        if (this.drawing.constrainToAxis && this.functionalityMode !== "MEASURETOOLS") {
          // raw vector from initial to cursor
          const dx = mouseX - this.drawing.inix;
          const dy = mouseY - this.drawing.iniy;

          // 1) compute its angle and length
          const angle = Math.atan2(dy, dx);               // in [–π, π]
          const length = Math.hypot(dx, dy);              // same as sqrt(dx*dx + dy*dy)

          // 2) snap angle to nearest multiple of 15° = π/12 radians
          const angleStep = 5
          const step = angleStep * Math.PI / 180;                      // angleStep in radians
          const snapped = Math.round(angle / step) * step;

          // 3) rebuild your end point at that angle, preserving length
          this.drawing.endx = this.drawing.inix + Math.cos(snapped) * length;
          this.drawing.endy = this.drawing.iniy + Math.sin(snapped) * length;
        } else {
          // free drawing
          this.drawing.endx = mouseX;
          this.drawing.endy = mouseY;
        }

        this.points = [
            this.screenToLocal(this.drawing.inix, this.drawing.iniy, distz),
            this.screenToLocal(this.drawing.endx, this.drawing.endy, distz)
        ];

        this.drawing.previewLine = this.createRulerLine(this.points, GraphicsRenderRuler.RULER_PREVIEW_LINE_COLOR);
        this.graphics.camera.add(this.drawing.previewLine);
        if (this.functionalityMode === "CUTPLATE") {
          this.renderCutLineDimensionRuler();
        }
        // Render gate is idle while Controls are disabled during ruler drawing —
        // request a frame so the preview follows the mouse.
        this.graphics.requestRender();

    } else if (this.drawing.buttonDown && buttons == 0 && this.drawing.init) {
        // End dragging
        this.drawing.init = false;
        this.drawing.buttonDown = false;
        this.graphics.Controls.enabled = true;

        ThreeJsCleanup.disposeObject(this.drawing.previewLine);
        this.drawing.previewLine = undefined;
        this.clearAreaDimensionRulers();
        this.graphics.requestRender();
        if (this.points.length < 2) {
          return null;
        }
        if(this.functionalityMode == "MEASURETOOLS"){
        this.createDecalGeometry();
        } else if(this.functionalityMode == "STIFFNERINSERTION"){
          return this.createStiffenerGeometry();
        } else if(this.functionalityMode == "CUTPLATE"){
          return this.createCutPlateGeometry();
        }

    }
    return null;
}

  private renderCutLineDimensionRuler(): void {
    const targetMesh = this.getCutLineDimensionTargetMesh();
    if (!targetMesh) {
      this.clearAreaDimensionRulers();
      return;
    }

    this.updateAreaDimensionRulers([{
      label: 'L',
      start: new THREE.Vector2(this.drawing.inix, this.drawing.iniy),
      end: new THREE.Vector2(this.drawing.endx, this.drawing.endy),
    }], [targetMesh]);
  }

  private getCutLineDimensionTargetMesh(): THREE.Object3D | null {
    const element = this.asset?.currentSelectedElement;
    if (!element || !(element instanceof StructuralElement)) {
      return null;
    }

    return element.SurfaceDesign;
  }




  public createDecalGeometry() {
    let line = [...this.points].map((v) =>
      this.graphics.camera.localToWorld(v))

    let offsetIntersectionPoint = []
    let intersectionPoint = []

    let allPlates = this.asset.GetVisibleGroups()

    // Flatten to visible meshes: the patched Raycaster does not recurse into
    // non-Mesh/Group/Sprite or invisible wrappers, so raycasting the campaign
    // groups directly misses the new-model meshes (same fix as click selection).
    this.sceneObjs = MeshUtils.collectVisibleMeshes(allPlates);

    var vectorWorldDirection = new THREE.Vector3();
    this.graphics.camera.getWorldDirection(vectorWorldDirection)

    let points = this.interpolate2DPoints({x:this.drawing.origx, y:this.drawing.origy},
      {x:this.drawing.endx, y:this.drawing.endy}, 20)

    let cameraDirection = new THREE.Vector3();
    this.graphics.Camera.getWorldDirection(cameraDirection);

    const hits = this.raycastScreenPolyline(points, cameraDirection, this.sceneObjs);
    for (const hit of hits) {
      intersectionPoint.push(hit.intersectionPoint);
      offsetIntersectionPoint.push(hit.displayPoint);
    }

    if (intersectionPoint.length > 1) {
      const measurementGroup = new THREE.Group();
      measurementGroup.name = 'RulerMeasurement';
      let distX = offsetIntersectionPoint[0].x - offsetIntersectionPoint[offsetIntersectionPoint.length-1].x
      let distY = offsetIntersectionPoint[0].y - offsetIntersectionPoint[offsetIntersectionPoint.length-1].y
      let distZ = offsetIntersectionPoint[0].z - offsetIntersectionPoint[offsetIntersectionPoint.length-1].z
      let meanX = (offsetIntersectionPoint[0].x + offsetIntersectionPoint[offsetIntersectionPoint.length-1].x) / 2
      let meanY = (offsetIntersectionPoint[0].y + offsetIntersectionPoint[offsetIntersectionPoint.length-1].y) / 2
      let meanZ = (offsetIntersectionPoint[0].z + offsetIntersectionPoint[offsetIntersectionPoint.length-1].z) / 2


      GraphicsRenderRuler.dist = Math.sqrt(distX * distX + distY * distY + distZ * distZ)
      let vertices = []

      for (let points of offsetIntersectionPoint) {
        vertices.push(points);
      }

      let rulerLine = this.createRulerLine(vertices);
      rulerLine.computeLineDistances();

      const endIndex = intersectionPoint.length - 1;
      const startConnector = this.createRulerLine([intersectionPoint[0], offsetIntersectionPoint[0]]);
      const endConnector = this.createRulerLine([intersectionPoint[endIndex], offsetIntersectionPoint[endIndex]]);

      measurementGroup.add(startConnector);
      measurementGroup.add(endConnector);

      measurementGroup.add(rulerLine);
      this.drawing.group.add(measurementGroup);
      if (!this.drawing.group.parent) {
        this.graphics.scene.add(this.drawing.group)
      }
      this.createTextGeometry(meanX, meanY, meanZ, GraphicsRenderRuler.dist, measurementGroup)
    }
  }

  public createStiffenerGeometry():{regionId:string, sectionId:string, elementId:string,
    negativeGeometry: THREE.BufferGeometry, positiveGeometry:THREE.BufferGeometry
  } {
    const session = this.stiffenerSession;
    if (!session?.canDraw) return null;
    const region = this.asset?.GetStructureVisibleRegions().find(r => r.visible);
    const section = region?.sections?.find(s => s.visible);
    const campaign = this.asset?.StructureCampaigns?.[0];
    const linearExample = campaign?.LinearElements?.find(x => x.SectionId === section?.Guid && x.regionId === region?.Guid);
    if (!region || !section || !linearExample?.properties) {
      session.reportDrawingError('Select a visible region and section with a reference linear element before drawing.');
      return null;
    }
    let line = [...this.points].map((v) =>
      this.graphics.camera.localToWorld(v))

    let offsetIntersectionPoint = []
    let intersectionPoint = []
    let normal = []

    let allPlates = this.asset.getStructureElements(true).filter(x => x.TypeId == ELEMENTSTYPESIDS.PLATE).map(x => x.SurfaceDesign);

    this.sceneObjs =allPlates;

    var vectorWorldDirection = new THREE.Vector3();
    this.graphics.camera.getWorldDirection(vectorWorldDirection)

    let points = this.interpolate2DPoints({x:this.drawing.origx, y:this.drawing.origy},
      {x:this.drawing.endx, y:this.drawing.endy}, 20)

    let cameraDirection = new THREE.Vector3();
    this.graphics.Camera.getWorldDirection(cameraDirection);

    for (const point of points) {
      this.rayPoints(point.x, point.y, cameraDirection, offsetIntersectionPoint, intersectionPoint, normal);
    }

    if (intersectionPoint.length > 1) {
      let distX = offsetIntersectionPoint[0].x - offsetIntersectionPoint[offsetIntersectionPoint.length-1].x
      let distY = offsetIntersectionPoint[0].y - offsetIntersectionPoint[offsetIntersectionPoint.length-1].y
      let distZ = offsetIntersectionPoint[0].z - offsetIntersectionPoint[offsetIntersectionPoint.length-1].z
      let meanX = (offsetIntersectionPoint[0].x + offsetIntersectionPoint[offsetIntersectionPoint.length-1].x) / 2
      let meanY = (offsetIntersectionPoint[0].y + offsetIntersectionPoint[offsetIntersectionPoint.length-1].y) / 2
      let meanZ = (offsetIntersectionPoint[0].z + offsetIntersectionPoint[offsetIntersectionPoint.length-1].z) / 2


      GraphicsRenderRuler.dist = Math.sqrt(distX * distX + distY * distY + distZ * distZ)
      let vertices = []

      const material = GraphicsMaterials.rulerLineMaterial;

      for (let points of offsetIntersectionPoint) {
        vertices.push(points);
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(vertices);

      let rulerLine = new THREE.Line(geometry, material);
      rulerLine.computeLineDistances();

      let line1 = []
      const geometry2 = []
      const curvePath = new THREE.CurvePath<THREE.Vector3>();

      vertices = this.uniqueValues(vertices);
      vertices = this.sortPointsToFormLineFacePlates(vertices);
      for (let i = 0; i < vertices.length - 1; i++) {
        const segment = new THREE.LineCurve3(vertices[i] as THREE.Vector3, vertices[i + 1] as THREE.Vector3);
        curvePath.add(segment);
      }
      // const segment = new THREE.LineCurve3(vertices[0] as THREE.Vector3, vertices[vertices.length - 1] as THREE.Vector3);
      // curvePath.add(segment);
      let length = curvePath.getLength();
      if (!Number.isFinite(length) || length <= 0) {
        session.reportDrawingError('Draw a line with a positive length across visible plates.');
        return null;
      }
      const bufferGeometry = new THREE.TubeGeometry(curvePath, 8, 0.01, 16, false);
      const line = new THREE.Mesh(bufferGeometry, GraphicsMaterials.redMaterial);
      line.geometry.computeBoundingBox();
      line.geometry.computeVertexNormals();

      const id = uuidv4();//.replace(/-/g, '') as string;
      let regionId = region.Guid;
      line.renderOrder = 1;
      line.geometry.computeBoundingBox();
      line.userData.FrontCompartmentID = regionId;
      line.userData.BackCompartmentID = regionId;
      line.userData.Class = 'Plate';
      line.userData.Type = 'Plate';
      line.userData.Area = 0;
      line.userData.Guid = id.replace(/-/g, '') as string;;
      line.userData.TypeId = ELEMENTSTYPESIDS.UNDEFINED;
      line.userData.RegionId = line.userData.FrontCompartmentID;
      line.userData.RegionIds = [line.userData.FrontCompartmentID];

      let points = []
      let normals = []
      // Find the point that corresponds to the drawing origin
      const originPoint = intersectionPoint.find(p =>
        Math.abs(p.x - this.screenToLocal(this.drawing.origx, this.drawing.origy, 1).x) < 0.001 &&
        Math.abs(p.y - this.screenToLocal(this.drawing.origx, this.drawing.origy, 1).y) < 0.001 &&
        Math.abs(p.z - this.screenToLocal(this.drawing.origx, this.drawing.origy, 1).z) < 0.001
      );

      // If we found the origin point, start with it
      if (originPoint) {
        points.push(originPoint.x, originPoint.y, originPoint.z);
      }

      // Add all other points in their original order
      for (const element of intersectionPoint) {
        if (!originPoint || !element.equals(originPoint)) {
          points.push(element.x, element.y, element.z);
        }
      }

      for (let i = 0; i < normal.length; i += 3) {
        normals.push(normal[i].x, normal[i].y, normal[i].z);
      }

      const {points: pointsFinal, normals: normalsFinal} = this.removeCollinearPoints(points, normals);
      line.userData.LineGeometry = pointsFinal;
      line.userData.SectionID = section.Guid;

      let linearElements = this.asset.StructureCampaigns[0].LinearElements.filter(x => x.SectionId === line.userData.SectionID && x.regionId === line.userData.RegionId
        && x.TypeId == ELEMENTSTYPESIDS.UNDEFINED
      );

      let count = (linearElements?.length ?? 0) + 1;

      const undefinedLinearElement = new LinearElement(line, GraphicsMaterials.redMaterial, line.userData.Guid);
      undefinedLinearElement.properties = JSON.parse(JSON.stringify(linearExample.properties));
      undefinedLinearElement.properties.General.FullBracketLabel = null;
      undefinedLinearElement.properties.General.FullBracketId = null;
      undefinedLinearElement.properties.General.FullStiffenerLabel = null;
      undefinedLinearElement.properties.General.FullStiffenerId = null;
      undefinedLinearElement.properties.General.CompleteBracketLabel = null;
      undefinedLinearElement.properties.General.CompleteBracketId = null;
      undefinedLinearElement.properties.General.CompleteStiffenerLabel = null;
      undefinedLinearElement.properties.General.CompleteStiffenerId = null;
      undefinedLinearElement.TypeId = ELEMENTSTYPESIDS.UNDEFINED;
      undefinedLinearElement.properties.TypeId = ELEMENTSTYPESIDS.UNDEFINED;
      undefinedLinearElement.properties.TypeName = 'UNDEFINED';
      undefinedLinearElement.properties.ElementId = id;
      undefinedLinearElement.properties.General.ElementId = id;
      undefinedLinearElement.properties.id = id;
      undefinedLinearElement.properties.General.Label = 'Undefined '+count;
      undefinedLinearElement.length = length;
      undefinedLinearElement.Line = pointsFinal;
      undefinedLinearElement.LineNormals = normalsFinal;
      this.asset.StructureCampaigns[0].addLinearStructureElement(undefinedLinearElement);
      if (!session.acceptDraft(undefinedLinearElement)) {
        undefinedLinearElement.section?.deleteLinearElementByIdOnly(undefinedLinearElement.Guid);
        return null;
      }

      return {regionId:regionId, sectionId:undefinedLinearElement.SectionId, elementId:undefinedLinearElement.Guid,
         negativeGeometry:null, positiveGeometry:null};
    }
  }

  public createCutPlateGeometry(): { regionId: string, sectionId: string, elementId: string,
    negativeGeometry: THREE.BufferGeometry, positiveGeometry:THREE.BufferGeometry
  } {
    if (
      this.asset.currentSelectedElement === null ||
      !(this.asset.currentSelectedElement instanceof StructuralElement)
    ) {
      return { regionId: null, sectionId: null, elementId: null,
        negativeGeometry:null, positiveGeometry:null};
    }

    const element = this.asset.currentSelectedElement;
    const elementMesh = element.SurfaceDesign;

    // Get the element's plane (your helper returns an object with normal and pointOnPlane).
    const elementPlaneData = GeometryExtrusionComputer.getPlaneFromMesh(elementMesh);
    if (!elementPlaneData) {
      console.error('Could not determine element plane');
      return { regionId: null, sectionId: null, elementId: null,
        negativeGeometry:null, positiveGeometry:null};
    }
    // Convert to a THREE.Plane instance.
    const elementPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
      elementPlaneData.normal,
      elementPlaneData.pointOnPlane
    );

    // --- Use Raycaster to Compute Accurate Intersection Points on the Element's Plane ---

    // Convert screen coordinates to normalized device coordinates (NDC)
    const mouseStart = new THREE.Vector2(
      (this.drawing.origx / window.innerWidth) * 2 - 1,
      - (this.drawing.origy / window.innerHeight) * 2 + 1
    );
    const mouseEnd = new THREE.Vector2(
      (this.drawing.endx / window.innerWidth) * 2 - 1,
      - (this.drawing.endy / window.innerHeight) * 2 + 1
    );

    const raycaster = new THREE.Raycaster();

    // Compute intersection for start point
    raycaster.setFromCamera(mouseStart, this.graphics.camera);
    const startRayEnd = raycaster.ray.origin.clone().add(raycaster.ray.direction.clone().multiplyScalar(10000));
    const worldStart = new THREE.Vector3();
    elementPlane.intersectLine(new THREE.Line3(raycaster.ray.origin, startRayEnd), worldStart);

    // Compute intersection for end point
    raycaster.setFromCamera(mouseEnd, this.graphics.camera);
    const endRayEnd = raycaster.ray.origin.clone().add(raycaster.ray.direction.clone().multiplyScalar(10000));
    const worldEnd = new THREE.Vector3();
    elementPlane.intersectLine(new THREE.Line3(raycaster.ray.origin, endRayEnd), worldEnd);

    // --- Compute the Cutting (Perpendicular) Plane Relative to the Screen ---
    // Compute the drawn line on the element (L)
    const lineVector = new THREE.Vector3().subVectors(worldEnd, worldStart).normalize();
    // Get the camera's view direction (C)
    const cameraDir = this.graphics.camera.getWorldDirection(new THREE.Vector3()).normalize();
    // Compute the cutting plane's normal as N = C × L
    const cutPlaneNormal = new THREE.Vector3().crossVectors(cameraDir, lineVector).normalize();
    // Create the perpendicular (cutting) plane using worldStart as the coplanar point.
    const perpendicularPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(cutPlaneNormal, worldStart);

    // For debugging: add a PlaneHelper to verify the cutting plane
    // const planeHelper = new THREE.PlaneHelper(perpendicularPlane, 10000, 0xff0000);
    // this.graphics.scene.add(planeHelper);

    // --- Perform the Mesh Cut using the New Perpendicular Plane ---
    const result = GeometryExtrusionComputer.cutMeshWithPlane(elementMesh, perpendicularPlane);
    if (result) {
      const { positiveGeometry, negativeGeometry } = result;

      // Create new meshes for each side
      // const positiveMesh = new THREE.Mesh(positiveGeometry, GraphicsMaterials.redMaterial);
      // const negativeMesh = new THREE.Mesh(negativeGeometry, GraphicsMaterials.blueMaterial);

      // Add the new meshes to your scene and remove the original mesh
      // this.graphics.scene.add(positiveMesh);
      // this.graphics.scene.add(negativeMesh);
      // this.graphics.scene.remove(elementMesh);

      return { regionId: null, sectionId: null, elementId: null,
        negativeGeometry:negativeGeometry, positiveGeometry:positiveGeometry};
    }

    return { regionId: null, sectionId: null, elementId: null,
      negativeGeometry:null, positiveGeometry:null};
  }

  private removeCollinearPoints(points: number[], normals: number[]): {points: number[], normals: number[]} {
    if (points.length < 9) return {points, normals}; // Need at least 3 points (9 values) to check collinearity
    let normalsFinal: number[] = []
    const result: number[] = [];
    result.push(points[0], points[1], points[2]); // Keep the first point
    normalsFinal.push(normals[0], normals[1], normals[2]);
    for (let i = 3; i < points.length - 3; i += 3) {
        const x1 = result[result.length - 3], y1 = result[result.length - 2], z1 = result[result.length - 1];
        const x2 = points[i], y2 = points[i + 1], z2 = points[i + 2];
        const x3 = points[i + 3], y3 = points[i + 4], z3 = points[i + 5];

        // Compute vectors
        const v1 = [x2 - x1, y2 - y1, z2 - z1];
        const v2 = [x3 - x2, y3 - y2, z3 - z2];

        // Compute cross product
        const crossX = v1[1] * v2[2] - v1[2] * v2[1];
        const crossY = v1[2] * v2[0] - v1[0] * v2[2];
        const crossZ = v1[0] * v2[1] - v1[1] * v2[0];

        // If cross product is zero, points are collinear (allow small floating point error)
        const collinear = Math.abs(crossX) < 1e-6 && Math.abs(crossY) < 1e-6 && Math.abs(crossZ) < 1e-6;

        if (!collinear) {
            result.push(x2, y2, z2); // Keep the middle point if not collinear
            normalsFinal.push(normals[i], normals[i + 1], normals[i + 2]);
        }
    }

    // Always keep the last point
    result.push(points[points.length - 3], points[points.length - 2], points[points.length - 1]);
    normalsFinal.push(normals[normals.length - 3], normals[normals.length - 2], normals[normals.length - 1]);

    return {points:result, normals:normalsFinal};
}

  private uniqueValues(array: Array<THREE.Vector3>): Array<THREE.Vector3> {
    const uniqueValuesArray = new Array<THREE.Vector3>();

    for (const value of array) {
        const index = uniqueValuesArray.findIndex(x => x.x === value.x && x.y === value.y && x.z === value.z)
        if (index === -1) {
            uniqueValuesArray.push(value);
        }
    }

    return uniqueValuesArray;
  }

  private sortPointsToFormLineFacePlates(line: Array<THREE.Vector3>): Array<THREE.Vector3> {
    const lineBox = new THREE.Box3();
    line.forEach(l => {
        lineBox.expandByPoint(l);
    });

    const size = new THREE.Vector3();
    lineBox.getSize(size);

    if (size.x > size.y && size.x > size.z) {
        line.sort((a, b) => a.x > b.x && 1 || -1)
    }
    else if (size.y > size.x && size.y > size.z) {
        line.sort((a, b) => a.y > b.y && 1 || -1)
    }
    else {
        line.sort((a, b) => a.z > b.z && 1 || -1)
    }

    let current = line[0];
    line.splice(0, 1);
    const newLine = [current];
    while (line.length > 1) {
        current = line.reduce((a, b) => current.distanceTo(a) < current.distanceTo(b) ? a : b);
        let index = line.findIndex(x => x.equals(current));
        if (index > -1) {
            line.splice(index, 1);
            newLine.push(current);
        }
    }
    //
    newLine.push(line[0]);


    return newLine;
  }

  public screenToLocal(screenX, screenY, localZ) {
    let ndcX = (screenX / window.innerWidth) * 2 - 1
    let ndcY = (-screenY / window.innerHeight) * 2 + 1
    let rayc = new THREE.Raycaster()
    rayc.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.graphics.camera)
    let newpos = new THREE.Vector3()
    rayc.ray.at(localZ, newpos)
    this.graphics.camera.worldToLocal(newpos)
    return newpos
  }

  public interpolate2DPoints(start: any, end: any, numPoints: number): any[] {
    const points: any[] = [];

    for (let i = 0; i <= numPoints + 1; i++) {
        const t = i / (numPoints + 1);
        const interpolatedPoint: any = {
            x: start.x + t * (end.x - start.x),
            y: start.y + t * (end.y - start.y)
        };
        points.push(interpolatedPoint);
    }

    return points;
  }

  private rayPoints(x, y, cameraDirection, offsetIntersectionPoint, intersectionPoint, normal?: Array<THREE.Vector3>){
    const hit = this.raycastScreenPoint(x, y, cameraDirection, this.sceneObjs);
    if (!hit) {
      return;
    }

    intersectionPoint.push(hit.intersectionPoint);
    offsetIntersectionPoint.push(hit.displayPoint);
  }

  private raycastScreenPolyline(points: ScreenPoint[], cameraDirection: THREE.Vector3, targetMeshes: THREE.Object3D[]): RulerRaycastHit[] {
    const hits: RulerRaycastHit[] = [];
    let previousPoint: ScreenPoint = null;
    let previousHit: RulerRaycastHit = null;

    for (const point of points) {
      const currentHit = this.raycastScreenPoint(point.x, point.y, cameraDirection, targetMeshes);

      if (!previousPoint) {
        this.addRaycastHit(hits, currentHit);
        previousPoint = point;
        previousHit = currentHit;
        continue;
      }

      if (!previousHit && currentHit) {
        this.addRaycastHit(hits, this.refineBoundaryHit(previousPoint, point, cameraDirection, targetMeshes));
        this.addRaycastHit(hits, currentHit);
      } else if (previousHit && currentHit) {
        this.addRaycastHit(hits, currentHit);
      } else if (previousHit && !currentHit) {
        this.addRaycastHit(hits, this.refineBoundaryHit(point, previousPoint, cameraDirection, targetMeshes));
      }

      previousPoint = point;
      previousHit = currentHit;
    }

    return hits;
  }

  private refineBoundaryHit(missPoint: ScreenPoint, hitPoint: ScreenPoint, cameraDirection: THREE.Vector3, targetMeshes: THREE.Object3D[]): RulerRaycastHit {
    let low = missPoint;
    let high = hitPoint;
    let bestHit = this.raycastScreenPoint(high.x, high.y, cameraDirection, targetMeshes);

    if (!bestHit) {
      return null;
    }

    for (let i = 0; i < GraphicsRenderRuler.RULER_BORDER_REFINEMENT_STEPS; i++) {
      const midpoint = {
        x: (low.x + high.x) / 2,
        y: (low.y + high.y) / 2,
      };
      const midpointHit = this.raycastScreenPoint(midpoint.x, midpoint.y, cameraDirection, targetMeshes);

      if (midpointHit) {
        high = midpoint;
        bestHit = midpointHit;
      } else {
        low = midpoint;
      }
    }

    return bestHit;
  }

  private raycastScreenPoint(x: number, y: number, cameraDirection: THREE.Vector3, targetMeshes: THREE.Object3D[]): RulerRaycastHit {
    if (!targetMeshes?.length) {
      return null;
    }

    const ndcX = (x / window.innerWidth) * 2 - 1;
    const ndcY = -(y / window.innerHeight) * 2 + 1;
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), this.graphics.camera);
    raycaster.params = {
      Mesh: { threshold: 100 },
      Line: { threshold: 0.1 },
      LOD: {},
      Points: { threshold: 100 },
      Sprite: {},
    };

    const intersection = raycaster.intersectObjects(targetMeshes, true)[0];
    if (!intersection) {
      return null;
    }

    const displayDirection = new THREE.Vector3(
      Number(cameraDirection.x.toFixed(3)),
      Number(cameraDirection.y.toFixed(3)),
      Number(cameraDirection.z.toFixed(3)),
    );
    const intersectionPoint = intersection.point.clone();
    const displayPoint = intersectionPoint.clone().addScaledVector(
      displayDirection,
      -GraphicsRenderRuler.RULER_DISPLAY_OFFSET
    );

    return { intersectionPoint, displayPoint };
  }

  private addRaycastHit(hits: RulerRaycastHit[], hit: RulerRaycastHit): void {
    if (!hit) {
      return;
    }

    const previousHit = hits[hits.length - 1];
    if (!previousHit || previousHit.intersectionPoint.distanceTo(hit.intersectionPoint) > GraphicsRenderRuler.RULER_POINT_EPSILON) {
      hits.push(hit);
    }
  }

  public createTextGeometry(meanX, meanY, meanZ, distance: number = GraphicsRenderRuler.dist, group: THREE.Group = this.drawing.group) {
    if (!group.parent) {
      return;
    }

    const message = (1000 * distance).toFixed(0) + " mm";
    const labelInfos = createSimpleGeometryCenterLabels2DObjects(
      group as any,
      [{ position: new THREE.Vector3(meanX, meanY, meanZ), label: message }] as any,
      this.graphics.camera as any,
      1,
      1,
      100,
      1,
      1
    );

    this.rulerLabelInfos.push(...labelInfos);
  }

  public updateAreaDimensionRulers(segments: AreaDimensionSegment[], targetMeshes: THREE.Object3D[]): void {
    this.clearAreaDimensionRulers();
    if (!segments?.length || !targetMeshes?.length) {
      return;
    }

    this.areaDimensionGroup.name = 'AreaDimensionRulers';
    this.graphics.scene.add(this.areaDimensionGroup);

    const cameraDirection = new THREE.Vector3();
    this.graphics.camera.getWorldDirection(cameraDirection);
    const displayOffset = cameraDirection.multiplyScalar(-0.002);

    const preparedSegments = [];
    const maxDistanceByLabel = new Map<string, number>();

    for (const segment of segments) {
      const surfacePoints = this.raycastSegmentToSurface(segment, targetMeshes);
      if (surfacePoints.length < 2) {
        continue;
      }

      const distance = this.measurePolylineLength(surfacePoints);
      if (distance <= 0) {
        continue;
      }

      const displayPoints = surfacePoints.map(point => point.clone().add(displayOffset));
      preparedSegments.push({ segment, surfacePoints, displayPoints, distance });

      if (segment.useMaxLabelDistance) {
        maxDistanceByLabel.set(segment.label, Math.max(maxDistanceByLabel.get(segment.label) ?? 0, distance));
      }
    }

    for (const { segment, surfacePoints, displayPoints, distance } of preparedSegments) {
      this.areaDimensionGroup.add(this.createRulerLine(displayPoints));
      this.areaDimensionGroup.add(this.createRulerLine([surfacePoints[0], displayPoints[0]]));
      this.areaDimensionGroup.add(this.createRulerLine([
        surfacePoints[surfacePoints.length - 1],
        displayPoints[displayPoints.length - 1]
      ]));

      if (segment.showLabel !== false) {
        const midpoint = this.getPolylineMidpoint(displayPoints, distance);
        const labelPosition = segment.labelOffsetPx ? this.offsetPointByScreenPixels(midpoint, segment.labelOffsetPx) : midpoint;
        const labelDistance = segment.useMaxLabelDistance ? maxDistanceByLabel.get(segment.label) ?? distance : distance;
        const message = `${segment.label}: ${(labelDistance * 1000).toFixed(0)} mm`;
        const labels = createSimpleGeometryCenterLabels2DObjects(
          this.areaDimensionGroup as any,
          [{ position: labelPosition, label: message }] as any,
          this.graphics.camera as any,
          1,
          1,
          100,
          1,
          1
        );
        this.areaDimensionLabelInfos.push(...labels);
      }
    }

    if (this.areaDimensionGroup.children.length === 0) {
      this.clearAreaDimensionRulers();
    }
    this.graphics.requestRender();
  }

  /**
   * Draws dimension rulers directly from world-space edge segments (used to align the rulers to
   * the orthographic preview rectangle/circle/ellipse, instead of the camera-view-aligned bounds).
   * Each segment's length is measured in world space; labels are placed at the segment midpoint,
   * offset outward (away from `center`) in screen space so they sit just outside the shape.
   */
  public updateAreaDimensionRulersFromWorldSegments(segments: WorldDimensionSegment[], center: THREE.Vector3): void {
    this.clearAreaDimensionRulers();
    if (!segments?.length) {
      return;
    }

    this.areaDimensionGroup.name = 'AreaDimensionRulers';
    this.graphics.scene.add(this.areaDimensionGroup);

    const cameraDirection = new THREE.Vector3();
    this.graphics.camera.getWorldDirection(cameraDirection);
    const displayOffset = cameraDirection.multiplyScalar(-0.002);

    for (const segment of segments) {
      const start = segment.start.clone().add(displayOffset);
      const end = segment.end.clone().add(displayOffset);
      const distance = start.distanceTo(end);
      if (distance <= 0) {
        continue;
      }

      this.areaDimensionGroup.add(this.createRulerLine([start, end]));

      if (segment.showLabel !== false) {
        const midpoint = this.getSegmentMidpoint(start, end);
        const outwardPx = this.screenOutwardPixels(midpoint, center, 26);
        const labelPosition = this.offsetPointByScreenPixels(midpoint, outwardPx);
        this.createAreaDimensionLabel(labelPosition, `${segment.label}: ${(distance * 1000).toFixed(0)} mm`);
      }
    }

    if (this.areaDimensionGroup.children.length === 0) {
      this.clearAreaDimensionRulers();
    }
  }

  /** Screen-space pixel offset pointing from `center` toward `point`, scaled to `distancePx`. */
  private screenOutwardPixels(point: THREE.Vector3, center: THREE.Vector3, distancePx: number): THREE.Vector2 {
    const p = point.clone().project(this.graphics.camera);
    const c = center.clone().project(this.graphics.camera);
    const dx = ((p.x - c.x) * window.innerWidth) / 2;
    const dy = (-(p.y - c.y) * window.innerHeight) / 2; // screen Y grows downward
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) {
      return new THREE.Vector2(0, distancePx);
    }
    return new THREE.Vector2((dx / len) * distancePx, (dy / len) * distancePx);
  }

  /**
   * The on-element surface points inside the dragged screen bounds (the same raycast scan
   * updateAreaDimensionRulersFromValidBounds measures with). Lets alternative ruler paths
   * (e.g. the ortho-preview frame rulers) measure only the element-overlapping extent, so
   * the ruler starts where the area touches the element instead of at the drag origin.
   */
  public getValidAreaSurfacePoints(bounds: AreaDimensionBounds, targetMeshes: THREE.Object3D[]): THREE.Vector3[] {
    if (!bounds || !targetMeshes?.length || bounds.maxX <= bounds.minX || bounds.maxY <= bounds.minY) {
      return [];
    }
    return this.collectValidAreaSurfacePoints(bounds, targetMeshes);
  }

  public updateAreaDimensionRulersFromValidBounds(bounds: AreaDimensionBounds, targetMeshes: THREE.Object3D[]): void {
    this.clearAreaDimensionRulers();
    if (!bounds || !targetMeshes?.length || bounds.maxX <= bounds.minX || bounds.maxY <= bounds.minY) {
      return;
    }

    const surfacePoints = this.collectValidAreaSurfacePoints(bounds, targetMeshes);
    if (surfacePoints.length < 2) {
      return;
    }

    const viewBounds = this.getViewAlignedBounds(surfacePoints);
    const width = viewBounds.maxRight - viewBounds.minRight;
    const height = viewBounds.maxUp - viewBounds.minUp;
    if (width <= 0 && height <= 0) {
      return;
    }

    this.areaDimensionGroup.name = 'AreaDimensionRulers';
    this.graphics.scene.add(this.areaDimensionGroup);

    const offset = viewBounds.forward.clone().multiplyScalar(-0.002);
    const bottomLeft = this.createPointFromViewCoords(viewBounds.minRight, viewBounds.minUp, viewBounds.centerForward, viewBounds).add(offset);
    const bottomRight = this.createPointFromViewCoords(viewBounds.maxRight, viewBounds.minUp, viewBounds.centerForward, viewBounds).add(offset);
    const topLeft = this.createPointFromViewCoords(viewBounds.minRight, viewBounds.maxUp, viewBounds.centerForward, viewBounds).add(offset);
    const diameterLabel = bounds.labelMode === 'diameter' ? `D: ${(Math.max(width, height) * 1000).toFixed(0)} mm` : null;
    let diameterLabelShown = false;

    if (width > 0 && height > 0) {
      this.areaDimensionGroup.add(this.createRulerLine([topLeft, bottomLeft, bottomRight]));
    } else if (width > 0) {
      this.areaDimensionGroup.add(this.createRulerLine([bottomLeft, bottomRight]));
    } else if (height > 0) {
      this.areaDimensionGroup.add(this.createRulerLine([bottomLeft, topLeft]));
    }

    if (width > 0) {
      const widthLabel = diameterLabel ?? `W: ${(width * 1000).toFixed(0)} mm`;
      const widthLabelPosition = this.offsetPointByScreenPixels(this.getSegmentMidpoint(bottomLeft, bottomRight), new THREE.Vector2(0, 24));
      this.createAreaDimensionLabel(widthLabelPosition, widthLabel);
      diameterLabelShown = !!diameterLabel;
    }

    if (height > 0) {
      if (!diameterLabel || !diameterLabelShown) {
        const heightLabel = diameterLabel ?? `H: ${(height * 1000).toFixed(0)} mm`;
        const heightLabelPosition = this.offsetPointByScreenPixels(this.getSegmentMidpoint(bottomLeft, topLeft), new THREE.Vector2(-48, 0));
        this.createAreaDimensionLabel(heightLabelPosition, heightLabel);
      }
    }

    if (this.areaDimensionGroup.children.length === 0) {
      this.clearAreaDimensionRulers();
    }
    this.graphics.requestRender();
  }

  public clearAreaDimensionRulers(): void {
    if (this.areaDimensionGroup.parent || this.areaDimensionGroup.children.length > 0) {
      ThreeJsCleanup.disposeObject(this.areaDimensionGroup);
    }
    this.areaDimensionGroup = new THREE.Group();
    this.areaDimensionGroup.name = 'AreaDimensionRulers';
    this.areaDimensionLabelInfos = [];
  }

  private raycastSegmentToSurface(segment: AreaDimensionSegment, targetMeshes: THREE.Object3D[]): THREE.Vector3[] {
    const surfacePoints = this.raycastScreenSegmentToSurface(segment.start, segment.end, targetMeshes);
    if (surfacePoints.length >= 2 || !segment.fallbackStart || !segment.fallbackEnd) {
      return surfacePoints;
    }

    const fallbackPoints = this.raycastFallbackSegmentToSurface(segment, targetMeshes);
    return fallbackPoints ?? surfacePoints;
  }

  private raycastScreenSegmentToSurface(start: THREE.Vector2, end: THREE.Vector2, targetMeshes: THREE.Object3D[]): THREE.Vector3[] {
    const screenLength = start.distanceTo(end);
    const sampleCount = Math.min(12, Math.max(2, Math.ceil(screenLength / 40)));
    const points: ScreenPoint[] = [];

    for (let i = 0; i <= sampleCount; i++) {
      const t = i / sampleCount;
      points.push({
        x: start.x + (end.x - start.x) * t,
        y: start.y + (end.y - start.y) * t,
      });
    }

    const cameraDirection = new THREE.Vector3();
    this.graphics.camera.getWorldDirection(cameraDirection);
    return this.raycastScreenPolyline(points, cameraDirection, targetMeshes)
      .map(hit => hit.intersectionPoint);
  }

  private raycastFallbackSegmentToSurface(segment: AreaDimensionSegment, targetMeshes: THREE.Object3D[]): THREE.Vector3[] {
    let invalidStart = segment.start;
    let invalidEnd = segment.end;

    for (let i = 1; i <= GraphicsRenderRuler.RULER_FALLBACK_SCAN_STEPS; i++) {
      const t = i / GraphicsRenderRuler.RULER_FALLBACK_SCAN_STEPS;
      const currentStart = segment.start.clone().lerp(segment.fallbackStart, t);
      const currentEnd = segment.end.clone().lerp(segment.fallbackEnd, t);
      const surfacePoints = this.raycastScreenSegmentToSurface(currentStart, currentEnd, targetMeshes);

      if (surfacePoints.length >= 2) {
        return this.refineFallbackSegmentToSurface(
          invalidStart,
          invalidEnd,
          currentStart,
          currentEnd,
          surfacePoints,
          targetMeshes,
        );
      }

      invalidStart = currentStart;
      invalidEnd = currentEnd;
    }

    return null;
  }

  private refineFallbackSegmentToSurface(
    invalidStart: THREE.Vector2,
    invalidEnd: THREE.Vector2,
    validStart: THREE.Vector2,
    validEnd: THREE.Vector2,
    validSurfacePoints: THREE.Vector3[],
    targetMeshes: THREE.Object3D[],
  ): THREE.Vector3[] {
    let lowStart = invalidStart;
    let lowEnd = invalidEnd;
    let highStart = validStart;
    let highEnd = validEnd;
    let bestSurfacePoints = validSurfacePoints;

    for (let i = 0; i < GraphicsRenderRuler.RULER_BORDER_REFINEMENT_STEPS; i++) {
      const midpointStart = new THREE.Vector2(
        (lowStart.x + highStart.x) / 2,
        (lowStart.y + highStart.y) / 2,
      );
      const midpointEnd = new THREE.Vector2(
        (lowEnd.x + highEnd.x) / 2,
        (lowEnd.y + highEnd.y) / 2,
      );
      const midpointSurfacePoints = this.raycastScreenSegmentToSurface(midpointStart, midpointEnd, targetMeshes);

      if (midpointSurfacePoints.length >= 2) {
        highStart = midpointStart;
        highEnd = midpointEnd;
        bestSurfacePoints = midpointSurfacePoints;
      } else {
        lowStart = midpointStart;
        lowEnd = midpointEnd;
      }
    }

    return bestSurfacePoints;
  }

  private measurePolylineLength(points: THREE.Vector3[]): number {
    let distance = 0;
    for (let i = 1; i < points.length; i++) {
      distance += points[i - 1].distanceTo(points[i]);
    }
    return distance;
  }

  private getPolylineMidpoint(points: THREE.Vector3[], totalDistance: number): THREE.Vector3 {
    const targetDistance = totalDistance / 2;
    let travelled = 0;

    for (let i = 1; i < points.length; i++) {
      const segmentDistance = points[i - 1].distanceTo(points[i]);
      if (travelled + segmentDistance >= targetDistance) {
        const t = segmentDistance === 0 ? 0 : (targetDistance - travelled) / segmentDistance;
        return points[i - 1].clone().lerp(points[i], t);
      }
      travelled += segmentDistance;
    }

    return points[points.length - 1].clone();
  }

  private collectValidAreaSurfacePoints(bounds: AreaDimensionBounds, targetMeshes: THREE.Object3D[]): THREE.Vector3[] {
    const scanlines = this.createAreaDimensionScanlines(bounds);
    if (scanlines.length === 0) {
      return [];
    }

    const cameraDirection = new THREE.Vector3();
    this.graphics.camera.getWorldDirection(cameraDirection);
    const points: THREE.Vector3[] = [];

    // Scan the whole drawn area, not only a single ruler edge, so irregular plates still report full valid extents.
    for (const scanline of scanlines) {
      if (scanline.start.distanceTo(scanline.end) <= 0.001) {
        const hit = this.raycastScreenPoint(scanline.start.x, scanline.start.y, cameraDirection, targetMeshes);
        this.addUniqueSurfacePoint(points, hit?.intersectionPoint);
        continue;
      }

      const samples = this.createScreenSegmentSamples(
        scanline.start,
        scanline.end,
        GraphicsRenderRuler.RULER_VALID_AREA_SCAN_STEP_PX,
        GraphicsRenderRuler.RULER_VALID_AREA_MAX_SEGMENT_SAMPLES,
      );
      const hits = this.raycastScreenPolyline(samples, cameraDirection, targetMeshes);
      for (const hit of hits) {
        this.addUniqueSurfacePoint(points, hit.intersectionPoint);
      }
    }

    return points;
  }

  private createAreaDimensionScanlines(bounds: AreaDimensionBounds): AreaDimensionScanline[] {
    const width = bounds.maxX - bounds.minX;
    const height = bounds.maxY - bounds.minY;
    const horizontalCount = Math.min(
      GraphicsRenderRuler.RULER_VALID_AREA_MAX_SCANLINES,
      Math.max(4, Math.ceil(height / GraphicsRenderRuler.RULER_VALID_AREA_SCAN_STEP_PX)),
    );
    const verticalCount = Math.min(
      GraphicsRenderRuler.RULER_VALID_AREA_MAX_SCANLINES,
      Math.max(4, Math.ceil(width / GraphicsRenderRuler.RULER_VALID_AREA_SCAN_STEP_PX)),
    );
    const scanlines: AreaDimensionScanline[] = [];

    for (let i = 0; i <= horizontalCount; i++) {
      const y = bounds.minY + (height * i / horizontalCount);
      const span = this.getAreaHorizontalSpan(bounds, y);
      if (span) {
        scanlines.push({
          start: new THREE.Vector2(span.min, y),
          end: new THREE.Vector2(span.max, y),
        });
      }
    }

    for (let i = 0; i <= verticalCount; i++) {
      const x = bounds.minX + (width * i / verticalCount);
      const span = this.getAreaVerticalSpan(bounds, x);
      if (span) {
        scanlines.push({
          start: new THREE.Vector2(x, span.min),
          end: new THREE.Vector2(x, span.max),
        });
      }
    }

    return scanlines;
  }

  private getAreaHorizontalSpan(bounds: AreaDimensionBounds, y: number): { min: number; max: number } | null {
    if (bounds.shapeType === 'rectangle') {
      return { min: bounds.minX, max: bounds.maxX };
    }

    const centerX = bounds.centerX ?? (bounds.minX + bounds.maxX) / 2;
    const centerY = bounds.centerY ?? (bounds.minY + bounds.maxY) / 2;
    const radiusX = bounds.shapeType === 'circle' ? bounds.radius : bounds.radiusX;
    const radiusY = bounds.shapeType === 'circle' ? bounds.radius : bounds.radiusY;
    if (!radiusX || !radiusY) {
      return null;
    }

    const normalizedY = (y - centerY) / radiusY;
    if (Math.abs(normalizedY) > 1) {
      return null;
    }

    const span = radiusX * Math.sqrt(Math.max(0, 1 - normalizedY * normalizedY));
    return { min: centerX - span, max: centerX + span };
  }

  private getAreaVerticalSpan(bounds: AreaDimensionBounds, x: number): { min: number; max: number } | null {
    if (bounds.shapeType === 'rectangle') {
      return { min: bounds.minY, max: bounds.maxY };
    }

    const centerX = bounds.centerX ?? (bounds.minX + bounds.maxX) / 2;
    const centerY = bounds.centerY ?? (bounds.minY + bounds.maxY) / 2;
    const radiusX = bounds.shapeType === 'circle' ? bounds.radius : bounds.radiusX;
    const radiusY = bounds.shapeType === 'circle' ? bounds.radius : bounds.radiusY;
    if (!radiusX || !radiusY) {
      return null;
    }

    const normalizedX = (x - centerX) / radiusX;
    if (Math.abs(normalizedX) > 1) {
      return null;
    }

    const span = radiusY * Math.sqrt(Math.max(0, 1 - normalizedX * normalizedX));
    return { min: centerY - span, max: centerY + span };
  }

  private createScreenSegmentSamples(start: THREE.Vector2, end: THREE.Vector2, stepPx: number, maxSamples: number): ScreenPoint[] {
    const screenLength = start.distanceTo(end);
    const sampleCount = Math.min(maxSamples, Math.max(2, Math.ceil(screenLength / stepPx)));
    const points: ScreenPoint[] = [];

    for (let i = 0; i <= sampleCount; i++) {
      const t = i / sampleCount;
      points.push({
        x: start.x + (end.x - start.x) * t,
        y: start.y + (end.y - start.y) * t,
      });
    }

    return points;
  }

  private addUniqueSurfacePoint(points: THREE.Vector3[], point: THREE.Vector3 | null | undefined): void {
    if (!point) {
      return;
    }

    if (!points.some(existingPoint => existingPoint.distanceTo(point) <= GraphicsRenderRuler.RULER_POINT_EPSILON)) {
      points.push(point);
    }
  }

  private getViewAlignedBounds(points: THREE.Vector3[]): ViewAlignedBounds {
    this.graphics.camera.updateMatrixWorld(true);
    const right = new THREE.Vector3().setFromMatrixColumn(this.graphics.camera.matrixWorld, 0).normalize();
    const up = new THREE.Vector3().setFromMatrixColumn(this.graphics.camera.matrixWorld, 1).normalize();
    const forward = new THREE.Vector3();
    this.graphics.camera.getWorldDirection(forward).normalize();

    let minRight = Infinity;
    let maxRight = -Infinity;
    let minUp = Infinity;
    let maxUp = -Infinity;
    let forwardSum = 0;

    for (const point of points) {
      const rightDistance = point.dot(right);
      const upDistance = point.dot(up);
      minRight = Math.min(minRight, rightDistance);
      maxRight = Math.max(maxRight, rightDistance);
      minUp = Math.min(minUp, upDistance);
      maxUp = Math.max(maxUp, upDistance);
      forwardSum += point.dot(forward);
    }

    return {
      right,
      up,
      forward,
      minRight,
      maxRight,
      minUp,
      maxUp,
      centerForward: forwardSum / points.length,
    };
  }

  private createPointFromViewCoords(rightDistance: number, upDistance: number, forwardDistance: number, bounds: ViewAlignedBounds): THREE.Vector3 {
    return bounds.right.clone().multiplyScalar(rightDistance)
      .add(bounds.up.clone().multiplyScalar(upDistance))
      .add(bounds.forward.clone().multiplyScalar(forwardDistance));
  }

  private getSegmentMidpoint(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3 {
    return start.clone().add(end).multiplyScalar(0.5);
  }

  private createAreaDimensionLabel(position: THREE.Vector3, message: string): void {
    const labels = createSimpleGeometryCenterLabels2DObjects(
      this.areaDimensionGroup as any,
      [{ position, label: message }] as any,
      this.graphics.camera as any,
      1,
      1,
      100,
      1,
      1
    );
    this.areaDimensionLabelInfos.push(...labels);
  }

  private offsetPointByScreenPixels(point: THREE.Vector3, offsetPx: THREE.Vector2): THREE.Vector3 {
    const projected = point.clone().project(this.graphics.camera);
    projected.x += (offsetPx.x / window.innerWidth) * 2;
    projected.y -= (offsetPx.y / window.innerHeight) * 2;
    return projected.unproject(this.graphics.camera);
  }

  public clearGeometries(){
    ThreeJsCleanup.disposeObject(this.drawing.previewLine);
    ThreeJsCleanup.disposeObject(this.drawing.group);
    this.clearAreaDimensionRulers();
    this.resetRulerGroup();
    this.resetDrawingState();
    this.graphics.Controls.enabled = true;
    this.graphics.requestRender();
  }

  private resetRulerGroup(): void {
    this.drawing.group = new THREE.Group();
    this.drawing.group.name = 'Ruler';
  }

  private resetDrawingState(): void {
    this.drawing.init = false;
    this.drawing.buttonDown = false;
    this.drawing.previewLine = undefined;
    this.drawing.startDrawing = false;
    this.points = [];
    this.rulerLabelInfos = [];
  }

  private createRulerLine(points: THREE.Vector3[], color: number = GraphicsRenderRuler.RULER_LINE_COLOR): Line2 {
    const geometry = new LineGeometry();
    geometry.setPositions(this.toLinePositions(points));

    const material = new LineMaterial({
      color,
      linewidth: GraphicsRenderRuler.RULER_LINE_WIDTH,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      transparent: true,
      opacity: 1,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    material.name = 'RulerLineMaterial';

    const line = new Line2(geometry, material);
    line.computeLineDistances();
    line.frustumCulled = false;
    line.renderOrder = 999;
    return line;
  }

  private toLinePositions(points: THREE.Vector3[]): number[] {
    const positions: number[] = [];
    for (const point of points) {
      positions.push(point.x, point.y, point.z);
    }
    return positions;
  }

}
